<template>
  <div class="w-96">
    <div class="grid grid-cols-1 gap-6">
      <yb-input
        v-model="apiKey"
        label="API Key"
        type="password"
        class="w-full"
        placeholder="Enter your API key"
        layout="material"
        help="The API key for the large language model service. Enter an arbitrary string for Ollama."
        data-test-id="llm-api-key"
      />

      <div class="relative w-full">
        <yb-input
          v-model="modelName"
          label="Model Name"
          type="text"
          class="w-full"
          placeholder="Enter your model name."
          layout="material"
          help="The name of the model, for example: gpt-4o; deepseek-chat; llama3.1:8b."
          data-test-id="llm-model-name"
          @focus="dropdownOpenModel = true"
          @blur="handleModelBlur"
        />

        <div
          v-if="dropdownOpenModel"
          class="absolute z-10 mt-1 bg-white border border-gray-300 rounded-md shadow min-w-full"
        >
          <div
            v-for="option in modelOptions"
            :key="option.value"
            class="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
            @mousedown.prevent="modelName = option.value; nextTick(() => dropdownOpenModel = false)"
          >
            {{ option.label }}
          </div>
        </div>
      </div>

      <div class="relative w-full">
        <yb-input
          v-model="baseURL"
          label="Base URL"
          type="text"
          class="w-full"
          placeholder="Enter the URL for your model service"
          layout="material"
          help="The URL for the model service, for example: https://api.openai.com."
          data-test-id="llm-base-url"
          @focus="dropdownOpenBaseURL = true"
          @blur="handleBaseURLBlur"
        />

        <div
          v-if="dropdownOpenBaseURL"
          class="absolute z-10 mt-1 bg-white border border-gray-300 rounded-md shadow min-w-full"
        >
          <div
            v-for="option in baseURLOptions"
            :key="option.value"
            class="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
            @mousedown.prevent="baseURL = option.value; nextTick(() => dropdownOpenBaseURL = false)"
          >
            {{ option.label }}
          </div>
        </div>
      </div>

      <div class="relative w-full">
        <yb-input
          v-model="embeddingModelName"
          label="Embedding Model Name"
          type="text"
          class="w-full"
          placeholder="Enter your text embedding model name"
          layout="material"
          help="The text embedding model is used to find the most relevant tables."
          data-test-id="embedding-model-name"
          @focus="dropdownOpenEmbed = true"
          @blur="handleEmbedBlur"
        />

        <div
          v-if="dropdownOpenEmbed"
          class="absolute z-10 mt-1 bg-white border border-gray-300 rounded-md shadow min-w-full"
        >
          <div
            v-for="option in embeddingOptions"
            :key="option.value"
            class="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
            @mousedown.prevent="embeddingModelName = option.value; nextTick(() => dropdownOpenEmbed = false)"
          >
            {{ option.label }}
          </div>
        </div>
      </div>

      <yb-input
        v-model="maxMCV"
        label="Most Common Values"
        :validation="v$.maxMCV"
        type="number"
        class="w-full"
        placeholder="0"
        layout="material"
        help="The maximum number of values sampled per column. Note, setting this to > 0 could result in sensitive data being sent to the model."
        data-test-id="llm-max-mcv"
      />

      <yb-input
        v-model="maxRelevantTables"
        label="Relevant Tables"
        :validation="v$.maxRelevantTables"
        type="number"
        class="w-full"
        placeholder=""
        layout="material"
        help="The number of most relevant tables to use."
        data-test-id="llm-max-relevant-tables"
      />
    </div>
  </div>
</template>

<script>
import { sync } from 'vuex-pathify'
import { useVuelidate } from '@vuelidate/core'
import { integer, minValue } from '@vuelidate/validators'
import { ref, nextTick } from 'vue'

export default {
  setup() {
    const v$ = useVuelidate()

    const dropdownOpenModel = ref(false)
    const dropdownOpenEmbed = ref(false)
    const dropdownOpenBaseURL = ref(false)

    const handleModelBlur = () => setTimeout(() => (dropdownOpenModel.value = false), 200)
    const handleEmbedBlur = () => setTimeout(() => (dropdownOpenEmbed.value = false), 200)
    const handleBaseURLBlur = () => setTimeout(() => (dropdownOpenBaseURL.value = false), 200)

    const modelOptions = [
      { value: 'o3-mini', label: 'o3-mini' },
      { value: 'deepseek-reasoner', label: 'DeepSeek Reasoner' },
      { value: 'gemma3:12b', label: 'gemma3: 12B (Ollama)' }
    ]

    const baseURLOptions = [
      { value: 'https://api.openai.com', label: 'https://api.openai.com' },
      { value: 'https://api.deepseek.com', label: 'https://api.deepseek.com' },
      { value: 'http://localhost:11434', label: 'http://localhost:11434 (Ollama)' }

    ]

    const embeddingOptions = [
      { value: 'text-embedding-3-small', label: 'text-embedding-3-small (OpenAI)' },
      { value: 'mxbai-embed-large', label: 'mxbai-embed-large (Ollama)' }
    ]

    return {
      v$,
      dropdownOpenModel,
      dropdownOpenEmbed,
      dropdownOpenBaseURL,
      handleModelBlur,
      handleEmbedBlur,
      handleBaseURLBlur,
      modelOptions,
      baseURLOptions,
      embeddingOptions
    }
  },
  computed: {
    apiKey: sync('settings/editor@apiKey', false),
    modelName: sync('settings/editor@modelName', false),
    baseURL: sync('settings/editor@baseURL', false),
    maxMCV: sync('settings/editor@maxMCV', false),
    embeddingModelName: sync('settings/editor@embeddingModelName', false),
    maxRelevantTables: sync('settings/editor@maxRelevantTables', false)
  },
  validations() {
    return {
      maxMCV: {
        integer,
        minValue: minValue(0)
      },
      maxRelevantTables: {
        integer,
        minValue: minValue(0)
      }
    }
  }
}
</script>
